import React, { useEffect, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Logout as LogoutIcon,
  List as ListIcon,
  Group as GroupIcon,
  Key as KeyIcon,
  UploadFile as UploadFileIcon,
  Alarm as AlarmIcon,
  Edit as EditIcon,
  Google as GoogleIcon,
  Dashboard as DashboardIcon,
  Celebration as CelebrationIcon,
  Assignment as AssignmentIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import features from '../config/features.js';
import '../styles/components/Sidebar.scss';
import logoWhite from '../assets/logos/logo-white.png'
import { getAppAuthorizedDomains } from '../api/appAuthorizedDomain.js'
import { useDomain } from '../context/DomainContext.js';
import { useAuth } from '../context/AuthContext.js';

function Sidebar() {
  const location = useLocation();
  const { logout, user } = useAuth();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [domains, setDomains] = useState([]);
  const { selectedDomain, setSelectedDomain } = useDomain(0);
  const [currentDomainName, setCurrentDomainName] = useState('');

  const routes = [
    { path: '/dashboard', name: 'Dashboard', type: 'link', icon: <DashboardIcon /> },
    { path: '/', name: 'Administración', type: 'title', icon: <ListIcon /> },
    { path: '/templateslist', name: 'Firmas de correo', type: 'link', icon: <EditIcon /> },
    ...(features.specialTemplatesEnabled
      ? [{ path: '/special-templates', name: 'Plantillas Cumpleaños y Aniversario', type: 'link', icon: <CelebrationIcon /> }]
      : []
    ),
    ...(features.specialRulesEnabled
      ? [{ path: '/special-rules', name: 'Reglas especiales', type: 'link', icon: <AssignmentIcon /> }]
      : []
    ),
    { path: '/keywords', name: 'Palabras clave', type: 'link', icon: <KeyIcon /> },
    { path: '/workplacesusers', name: 'Cuentas de dominio', type: 'link', icon: <GoogleIcon /> },
    { path: '/', name: 'Configuración', type: 'title', icon: <ListIcon /> },
    { path: '/general-settings', name: 'Configuración general', type: 'link', icon: <SettingsIcon /> },
    { path: '/userslist', name: 'Usuarios', type: 'link', icon: <GroupIcon /> },
    { path: '/schedulertasks', name: 'Tareas programadas', type: 'link', icon: <AlarmIcon /> },
    { path: '/loaddata', name: 'Importar cuentas de dominio', type: 'link', icon: <UploadFileIcon /> },
    { path: '/', name: 'Mi cuenta', type: 'title', icon: <ListIcon /> },
    { path: '/cerrar-sesion', name: 'Cerrar sesión', type: 'action', icon: <LogoutIcon />, action: logout },
  ].filter(route => user.isAdmin || route.path !== "/loaddata");

  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await getAppAuthorizedDomains();
        setDomains(response.data);
        if (response?.data?.length > 0) {
          setSelectedDomain(response.data[0].id);
          setCurrentDomainName(response.data[0].domain);
        }
      } catch (error) {
        console.error('Error fetching domains:', error);
      }
    };

    fetchDomains();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentRoute = routes.find(route => route.path === location.pathname);
    const routeName = currentRoute?.name || 'Dashboard';

    if (currentDomainName) {
      document.title = `${currentDomainName} | ${routeName}`;
    }
  }, [location.pathname, currentDomainName, routes]);

  useEffect(() => {
    const currentRoute = location.pathname === '/' ? 0 : routes.findIndex(route => route.path === location.pathname);
    if (currentRoute) {
      setSelectedIndex(currentRoute);
    } else {
      setSelectedIndex(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleListItemClick = (index) => {
    setSelectedIndex(index);
  };

  const handleDomainChange = (event) => {
    const newDomainId = event.target.value;
    setSelectedDomain(newDomainId);

    const selectedDomainObj = domains.find(domain => domain.id === newDomainId);
    if (selectedDomainObj) {
      setCurrentDomainName(selectedDomainObj.domain);
    }
  };

  return (
    <Drawer
      variant="permanent"
      className="sidebar"
    >
      <Box className="logo-container">
        <Link to="/" style={{ textDecoration: "none" }} onClick={() => { handleListItemClick(0) }}>
          <img src={logoWhite} alt="Logo" />
        </Link>
      </Box>
      <div className="separator" />
      <Divider />
      <FormControl variant="standard" sx={{ margin: '16px' }}>
        <InputLabel id="domain-select-label" className="custom-select" shrink>Dominio</InputLabel>
        <Select
          labelId="domain-select-label"
          defaultValue={selectedDomain || ""}
          value={selectedDomain ?? ""}
          onChange={handleDomainChange}
          displayEmpty
          sx={{
            '&.Mui-focused': { pointerEvents: 'none' },
          }}
        >
          {Array.isArray(domains) && domains?.map((domain) => (
            <MenuItem key={domain.id} value={domain.id} className="custom-menu-item">
              {domain.domain}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <List>
        {Array.isArray(routes) && routes.map((r, index) => (r.type === 'title' ?
          <ListItem key={r.name + '' + index}>
            <ListItemText primary={r.name} sx={{ fontWeight: 'bold', color: '#ffffff' }} />
          </ListItem>
          : r.type === 'action' ? (
            <ListItem
              component={Link}
              button="true"
              key={index}
              onClick={r.action}
              sx={{
                bgcolor: 'transparent',
                '&:hover': { bgcolor: '#e0e0e0' },
              }}
            >
              <ListItemIcon>{r.icon}</ListItemIcon>
              <ListItemText primary={r.name} />
            </ListItem>
          ) : <ListItem
            key={r.path}
            button="true"
            component={Link}
            to={r.path}
            selected={selectedIndex === index}
            onClick={() => handleListItemClick(index)}
            sx={{ bgcolor: selectedIndex === index ? '#2196F3' : 'transparent' }}
          >
            <ListItemIcon>{r.icon}</ListItemIcon>
            <ListItemText primary={r.name} />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
}

export default Sidebar;